import { Row, Col, Button, Form, Input, Select, Modal } from "antd";
import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { useDispatch } from "react-redux";
import userService from "../../services/user-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import constantService from "../../services/constant-service";
import {
  WEBSITE_USER_ROLE,
  USER_STATUS,
} from "../../constants/app-constants";

const { Option } = Select;

// Create/edit a website user. Same service + endpoints as the admin user screen
// (POST /user/register, POST /user/edit) with `userRole` pinned to WEBSITE_USER.
const AddEditWebsiteUserDialog = ({
  isShow,
  onCancel,
  onSaveComplete,
  editData,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [userStatus, setUserStatus] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function initData() {
      const { userStatus } = await constantService.getUserStatus();
      setUserStatus(userStatus);
    }

    if (isShow) {
      initData();
    } else {
      setUserStatus([]);
    }
  }, [isShow]);

  useEffect(() => {
    if (isShow && editData) {
      form.setFieldsValue({
        username: editData.username,
        firstName: editData.profile?.firstName,
        lastName: editData.profile?.lastName,
        email: editData.profile?.email,
        telNo: editData.profile?.telNo,
        status: editData.status,
      });
    } else if (isShow) {
      form.setFieldsValue({
        username: null,
        userPassword: null,
        firstName: null,
        lastName: null,
        email: null,
        telNo: null,
        status: USER_STATUS.ACTIVE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const renderUserStatusOptions = () => {
    return userStatus
      .filter((item) => item.value !== USER_STATUS.DELETED)
      .map((item, index) => (
        <Option key={index} value={item.value}>
          {item.labelEn}
        </Option>
      ));
  };

  const onSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      try {
        dispatch(appLoaderAction.enable());

        if (!editData) {
          const bean = {
            username: values.username,
            userPassword: values.userPassword,
            userRole: WEBSITE_USER_ROLE,
            status: values.status,
            profile: {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              telNo: values.telNo,
            },
          };
          await userService.add(bean);
        } else {
          const bean = JSON.parse(JSON.stringify(editData));
          bean.userRole = WEBSITE_USER_ROLE;
          bean.status = values.status;
          bean.profile = bean.profile || {};
          bean.profile.email = values.email;
          bean.profile.firstName = values.firstName;
          bean.profile.lastName = values.lastName;
          bean.profile.telNo = values.telNo;
          await userService.edit(bean);
        }

        dispatch(appLoaderAction.disale());
        onSaveComplete();
        notifyUtitls.success("สำเร็จ", "บันทึกสำเร็จ");
      } catch (err) {
        dispatch(appLoaderAction.disale());
        notifyUtitls.error(err);
      }
    } catch (errorInfo) {
      notifyUtitls.warning("แจ้งเตือน", "โปรดระบุข้อมูลให้ครบถ้วน");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container h-100">
      <div className="title-page">
        <Modal
          title={editData ? "แก้ไขผู้ใช้งานเว็บไซต์" : "เพิ่มผู้ใช้งานเว็บไซต์"}
          centered
          width={750}
          visible={isShow}
          onCancel={onCancel}
          destroyOnClose
          footer={[
            <Button key="submit" type="primary" onClick={onSave} loading={loading}>
              <Icon.Save className="me-2" size={17} />
              บันทึก
            </Button>,
            <Button key="back" className="btn-gray" onClick={onCancel}>
              ยกเลิก
            </Button>,
          ]}
        >
          <Form
            layout="vertical"
            name="add_edit_website_user"
            preserve={false}
            form={form}
          >
            <Row gutter={[30, 5]}>
              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="รหัสผู้ใช้งาน"
                  name="username"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input disabled={editData ? true : false} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="รหัสผ่าน"
                  name="userPassword"
                  className="mb-4"
                  hidden={editData ? true : false}
                  rules={[
                    {
                      required: editData ? false : true,
                    },
                  ]}
                >
                  <Input.Password name="userPassword" className="w-100 h-auto" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="ชื่อ"
                  name="firstName"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="นามสกุล"
                  name="lastName"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="อีเมล"
                  name="email"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      type: "email",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="เบอร์โทรศัพท์"
                  name="telNo"
                  className="mb-4"
                  rules={[
                    () => ({
                      validator(rule, value) {
                        const numbers = /^[0-9]+$/;
                        if (!value || value.match(numbers)) {
                          return Promise.resolve();
                        }
                        return Promise.reject("โปรดระบุตัวเลขเท่านั้น");
                      },
                    }),
                  ]}
                >
                  <Input maxLength={10} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={12}>
                <Form.Item
                  label="สถานะ"
                  name="status"
                  initialValue={USER_STATUS.ACTIVE}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select>{renderUserStatusOptions()}</Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AddEditWebsiteUserDialog;
